import { useCallback } from 'react';
import {
	useSetTradeFormBaseSize,
	useSetTradeFormQuoteSize,
} from 'src/components/TradeForm/hooks/useSetTradeFormInputState';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import { SizeUnit } from 'src/components/TradeForm/SizeInput/types';
import { TradeFormHandleSizeInputChange } from 'src/components/TradeForm/types';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { roundToStepSizeIfLargeEnough } from 'src/utils/math';

interface Params {
	stepSize: number;
}

export default function useTradeFormHandleSizeInputChange({
	stepSize,
}: Params) {
	const setTradeFormMaxLeverageSelected = useDriftStore(
		(s) => s.setTradeFormMaxLeverageSelected
	);
	const setFormQuoteSize = useSetTradeFormQuoteSize();
	const setFormBaseSize = useSetTradeFormBaseSize();

	const [leadSide] = useSizeInputSource();

	const set = useDriftStore((s) => s.set);
	const storeLeadSideInTradeForm = useCallback(
		(side: SizeUnit) => {
			set((s) => {
				s.tradeForm.leadSide = side;
			});
		},
		[set]
	);

	return useCallback<TradeFormHandleSizeInputChange>(
		(value, { sizeUnit } = {}) => {
			// Turn off max leverage for user-initiated change.
			setTradeFormMaxLeverageSelected(false);

			const baseSizeRoundedToStepSize = roundToStepSizeIfLargeEnough(
				value,
				stepSize
			);

			// Some updates only operate on either quote or base. So we need to
			// temporarily force one or the other for any dependent logic to run
			// correctly, then switch back to the true lead side after.
			if (sizeUnit) {
				storeLeadSideInTradeForm(sizeUnit);

				if (sizeUnit === 'base') {
					setFormBaseSize(baseSizeRoundedToStepSize);
				} else {
					setFormQuoteSize(value);
				}

				setTimeout(() => {
					storeLeadSideInTradeForm(leadSide);
				}, 1);

				return;
			}

			storeLeadSideInTradeForm(leadSide);

			if (leadSide === 'base') {
				setFormBaseSize(baseSizeRoundedToStepSize);
			} else {
				setFormQuoteSize(value);
			}
		},
		[
			setFormBaseSize,
			setFormQuoteSize,
			storeLeadSideInTradeForm,
			setTradeFormMaxLeverageSelected,
			leadSide,
			stepSize,
		]
	);
}
